import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import router from 'next/router';
import Link from 'next/link';
import styles from '@components/Message/Message.styles';
import theme from '@styles/theme';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import MessageTimeBar from '@components/Message/MessageTimeBar';

type PropsType = {
  message: React.ReactNode;
  backgroundColor?: string;
  color?: string;
  autoCloseWithDelay?: boolean;
  delay?: number;
  onClose: VoidFunction;
  marginBottom: number;
  marginTop: number;
  displayBarTime?: number;
  isOnCloseAnimation?: boolean;
  attachRefreshLink?: boolean;
  useClose?: boolean;
  link?: string;
  maxWidth?: string;
};

const Message = ({
  message,
  backgroundColor = theme.rawColors.dirtyPink200,
  color = theme.rawColors.darkGrey700,
  autoCloseWithDelay = false,
  delay = 1000,
  onClose,
  marginTop,
  marginBottom,
  displayBarTime = 0,
  isOnCloseAnimation = true,
  attachRefreshLink = false,
  useClose = true,
  link = undefined,
  maxWidth = undefined,
}: PropsType) => {
  const [closed, setClosed] = useState(false);
  const { t } = useTranslation('common');
  const { t: tLayouts } = useTranslation('layouts');

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (autoCloseWithDelay) {
      timer = setTimeout(() => {
        if (isOnCloseAnimation) setClosed(true);
        onClose();
      }, delay);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [autoCloseWithDelay]);

  const handleCloseMessage = () => {
    if (isOnCloseAnimation) setClosed(true);
    onClose();
  };

  const handleRefresh = () => {
    router.reload();
  };

  return (
    <div
      sx={styles.container(closed, marginTop, marginBottom, color, backgroundColor, maxWidth)}
      data-cy="Message"
      className="message-container"
      role="alert"
    >
      <div sx={styles.infoIconStyles(closed)}>
        <Icon icon="InfoOutline" size="24px" color={color} />
      </div>
      <div sx={styles.message(closed, color)}>
        {link ? (
          <Link
            href={link || '/'}
            target="_blank"
            title={`${typeof message === 'string' ? message : 'Link'} ${tLayouts('footer.links.opensInNewWindow')}`}
          >
            {message}{' '}
          </Link>
        ) : (
          <>{message} </>
        )}

        {attachRefreshLink && (
          <div
            sx={styles.refresh}
            onClick={handleRefresh}
            onKeyDown={(e) => e.key === 'Enter' && handleRefresh()}
            role="button"
            tabIndex={0}
          >
            {t('alerts.configurator.clickToRefresh')}
          </div>
        )}
      </div>
      <div sx={styles.closeButtonStyles(closed, color)}>
        {useClose && (
          <Button variant="iconOnly" sx={styles.buttonStyles(closed)} onClick={handleCloseMessage}>
            <Icon icon="Close2" size="16px" color={color} />
          </Button>
        )}
      </div>
      {displayBarTime > 0 && <MessageTimeBar displayTime={displayBarTime} />}
    </div>
  );
};

export default Message;
